import { hasMinRole, type SystemRole } from "@/lib/constants";
import type { FlagResolver } from "./registry";
import type { ReleaseItem, ReleaseNote, ReleaseTheme } from "./release-types";

export { RELEASE_NOTES } from "./release-entries";

/** Laveste rolle som får se systemrapporten (per-PR-loggen). */
export const SYSTEM_REPORT_MIN_ROLE: SystemRole = "superadmin";

export function canSeeSystemReport(role: string | null | undefined): boolean {
  return hasMinRole(role ?? "user", SYSTEM_REPORT_MIN_ROLE);
}

/**
 * Filtrerer utrullinger på rolle og feature-flagg, på tre nivåer: hele
 * utrullingen, temaet og punktet. Tomme temaer fjernes, og en utrulling uten
 * ett eneste synlig punkt (heller ikke under «På vei» eller «Rettinger»)
 * forsvinner helt. Da kan den heller ikke gi popup for noe brukeren ikke ser.
 */
export function visibleReleases(
  releases: readonly ReleaseNote[],
  role: string | null | undefined,
  isFlagOn: FlagResolver,
): ReleaseNote[] {
  const effectiveRole = role ?? "user";
  const synlig = (x: { minRole?: SystemRole; featureFlag?: ReleaseItem["featureFlag"] }) =>
    (!x.minRole || hasMinRole(effectiveRole, x.minRole)) && (!x.featureFlag || isFlagOn(x.featureFlag));
  const items = (list: readonly ReleaseItem[] | undefined) => (list ?? []).filter(synlig);

  const result: ReleaseNote[] = [];
  for (const release of releases) {
    if (release.minRole && !hasMinRole(effectiveRole, release.minRole)) continue;
    const themes: ReleaseTheme[] = release.themes
      .filter(synlig)
      .map((t) => ({ ...t, items: items(t.items) }))
      .filter((t) => t.items.length > 0);
    const upcoming = items(release.upcoming);
    const fixes = items(release.fixes);
    if (themes.length + upcoming.length + fixes.length === 0) continue;
    result.push({ ...release, themes, upcoming, fixes });
  }
  return result;
}

/** Et segment av en punkttittel: vanlig tekst eller én tast. */
export type ReleaseTextSegment = { kind: "text"; text: string } | { kind: "key"; text: string };

/**
 * Deler en punkttittel i tekst og taster. Alt mellom backticks er taster, og
 * hvert ord blir sin egen tast, så `⌘ K` vises som to tastetegn. En backtick
 * uten partner tolkes som tekst, så en skrivefeil i innholdet aldri sluker
 * resten av tittelen.
 */
export function splitReleaseText(text: string): ReleaseTextSegment[] {
  const segments: ReleaseTextSegment[] = [];
  const parts = text.split("`");
  const harPartner = parts.length % 2 === 1;
  parts.forEach((part, i) => {
    const erTaster = i % 2 === 1 && (harPartner || i < parts.length - 1);
    if (erTaster) {
      for (const key of part.trim().split(/\s+/).filter(Boolean)) segments.push({ kind: "key", text: key });
    } else {
      const tekst = !harPartner && i === parts.length - 1 && i > 0 ? `\`${part}` : part;
      if (tekst) segments.push({ kind: "text", text: tekst });
    }
  });
  return segments;
}
